import "./styles.css";

declare global {
  interface Window {
    Office?: any;
  }
}

type MailItem = {
  subject: string;
  htmlBody: string;
  textBody: string;
};

const app = document.querySelector<HTMLElement>("#app");

if (!app) {
  throw new Error("Missing #app root");
}

app.innerHTML = `
  <section class="shell">
    <header class="topbar">
      <div>
        <p class="eyebrow">Outlook Add-in Test</p>
        <h1 id="message-title">Loading message...</h1>
      </div>
      <button id="refresh-button" type="button">Refresh</button>
    </header>
    <section class="status" id="status">Waiting for Office.js.</section>
    <section class="layout" aria-label="Selected email">
      <aside class="pane meta-pane">
        <h2>Message</h2>
        <dl>
          <div>
            <dt>Subject</dt>
            <dd id="subject-value">-</dd>
          </div>
          <div>
            <dt>From</dt>
            <dd id="from-value">-</dd>
          </div>
          <div>
            <dt>Received</dt>
            <dd id="received-value">-</dd>
          </div>
          <div>
            <dt>Item ID</dt>
            <dd id="item-id-value">-</dd>
          </div>
        </dl>
        <button id="token-button" type="button" class="secondary">Test SSO token</button>
        <p id="token-result" class="token-result">SSO has not been tested.</p>
      </aside>
      <section class="pane body-pane">
        <div class="body-header">
          <h2>Content</h2>
          <div class="segmented" role="tablist" aria-label="Body format">
            <button id="html-tab" type="button" class="active">HTML</button>
            <button id="text-tab" type="button">Text</button>
          </div>
        </div>
        <iframe id="html-body" title="Email body"></iframe>
        <pre id="text-body" hidden></pre>
      </section>
    </section>
  </section>
`;

const state: MailItem = {
  subject: "",
  htmlBody: "",
  textBody: ""
};

const statusEl = byId<HTMLElement>("status");
const titleEl = byId<HTMLElement>("message-title");
const subjectEl = byId<HTMLElement>("subject-value");
const fromEl = byId<HTMLElement>("from-value");
const receivedEl = byId<HTMLElement>("received-value");
const itemIdEl = byId<HTMLElement>("item-id-value");
const htmlFrame = byId<HTMLIFrameElement>("html-body");
const textBody = byId<HTMLPreElement>("text-body");
const htmlTab = byId<HTMLButtonElement>("html-tab");
const textTab = byId<HTMLButtonElement>("text-tab");

byId<HTMLButtonElement>("refresh-button").addEventListener("click", () => {
  void loadCurrentMessage();
});

byId<HTMLButtonElement>("token-button").addEventListener("click", () => {
  void testSsoToken();
});

htmlTab.addEventListener("click", () => showBodyMode("html"));
textTab.addEventListener("click", () => showBodyMode("text"));

if (window.Office?.onReady) {
  window.Office.onReady(() => {
    void loadCurrentMessage();
  });
} else {
  setStatus("Office.js did not load. Open this page inside Outlook to inspect a message.", "error");
}

async function loadCurrentMessage(): Promise<void> {
  try {
    const item = window.Office?.context?.mailbox?.item;
    if (!item) {
      setStatus("No Outlook mail item is available. Open a message in Outlook and launch the add-in.", "error");
      return;
    }

    setStatus("Reading selected email...", "loading");

    const subject = item.subject || "(No subject)";
    const html = await getBody(item, window.Office.CoercionType.Html);
    const text = await getBody(item, window.Office.CoercionType.Text);

    state.subject = subject;
    state.htmlBody = html || "<p>(No HTML body returned.)</p>";
    state.textBody = text || "(No text body returned.)";

    titleEl.textContent = subject;
    subjectEl.textContent = subject;
    fromEl.textContent = formatEmailAddress(item.from);
    receivedEl.textContent = item.dateTimeCreated ? new Date(item.dateTimeCreated).toLocaleString() : "-";
    itemIdEl.textContent = item.itemId || "(not available in this mode)";
    textBody.textContent = state.textBody;
    htmlFrame.srcdoc = buildFrameDocument(state.htmlBody);

    setStatus("Message loaded.", "ready");
  } catch (error) {
    setStatus(error instanceof Error ? error.message : "Failed to read message.", "error");
  }
}

async function testSsoToken(): Promise<void> {
  const result = byId<HTMLElement>("token-result");
  const auth = window.Office?.context?.auth;
  if (!auth?.getAccessToken) {
    result.textContent = "Office SSO is not available in this host.";
    return;
  }

  result.textContent = "Requesting Office SSO token...";
  try {
    const token = await auth.getAccessToken({
      allowConsentPrompt: true,
      allowSignInPrompt: true
    });
    result.textContent = `Token received (${token.length} characters).`;
  } catch (error) {
    result.textContent = error instanceof Error ? error.message : "SSO token request failed.";
  }
}

function getBody(item: any, coercionType: string): Promise<string> {
  return new Promise((resolve, reject) => {
    item.body.getAsync(coercionType, (result: any) => {
      if (result.status === window.Office.AsyncResultStatus.Succeeded) {
        resolve(result.value || "");
      } else {
        reject(new Error(result.error?.message || "Outlook did not return the message body."));
      }
    });
  });
}

function showBodyMode(mode: "html" | "text"): void {
  const htmlActive = mode === "html";
  htmlFrame.hidden = !htmlActive;
  textBody.hidden = htmlActive;
  htmlTab.classList.toggle("active", htmlActive);
  textTab.classList.toggle("active", !htmlActive);
}

function buildFrameDocument(body: string): string {
  return `<!doctype html>
<html>
  <head>
    <meta charset="UTF-8" />
    <base target="_blank" />
    <style>
      body { color: #1f2937; font: 14px/1.5 system-ui, -apple-system, Segoe UI, sans-serif; margin: 16px; }
      img { max-width: 100%; height: auto; }
      table { max-width: 100%; }
      a { color: #0f6cbd; }
    </style>
  </head>
  <body>${body}</body>
</html>`;
}

function formatEmailAddress(value: any): string {
  if (!value) {
    return "-";
  }
  const name = value.displayName || "";
  const email = value.emailAddress || "";
  return [name, email && `<${email}>`].filter(Boolean).join(" ");
}

function setStatus(message: string, tone: "loading" | "ready" | "error"): void {
  statusEl.textContent = message;
  statusEl.dataset.tone = tone;
}

function byId<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) {
    throw new Error(`Missing element #${id}`);
  }
  return element as T;
}
